import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import {
    MDBBtn, MDBModal, MDBModalDialog, MDBModalContent, MDBModalHeader, MDBModalTitle, MDBModalBody, MDBModalFooter
} from 'mdb-react-ui-kit';
import classroomExamService from '../../../services/classroomExamService';
import types from '../../../services/types';
import Loading from '../../../components/Loading';
import ClassroomQuestionForm from '../../../components/Forms/ClassroomQuestionForm';
import sanitizeHtml from '../../../utils/sanitizeHtml';
import '../AulaVirtual.css';

const TYPE_LABEL = {
    [types.classroomQuestionTypes.VERDADERO_FALSO]: 'Verdadero / Falso',
    [types.classroomQuestionTypes.OPCION_MULTIPLE]: 'Opción múltiple',
    [types.classroomQuestionTypes.OPCION_UNICA]: 'Opción única',
    [types.classroomQuestionTypes.SUBIR_ARCHIVO]: 'Subir archivo',
    [types.classroomQuestionTypes.PREGUNTA_ABIERTA]: 'Pregunta abierta'
};

// Banco de preguntas del aula (requisito 6), reutilizable entre exámenes.
const QuestionBank = () => {
    const { classroomId } = useParams();
    const [preguntas, setPreguntas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modal, setModal] = useState(null); // { data } | null
    const [showModal, setShowModal] = useState(false); // controla MDBModal por separado del contenido
    // Fuerza que ClassroomQuestionForm se desmonte y monte de nuevo en cada
    // apertura (mismo motivo que en ClassroomManage.js): si no, al editar
    // una pregunta distinta el formulario se queda con los datos de la
    // primera pregunta que se abrió, en vez de la seleccionada.
    const [modalToken, setModalToken] = useState(0);

    const load = async () => {
        try {
            const data = await classroomExamService.getQuestions(classroomId);
            setPreguntas(data || []);
        } catch (error) {
            console.error(error);
        }
        setLoading(false);
    };

    useEffect(() => { load(); }, [classroomId]);

    const openModal = (config) => {
        setModal(config);
        setModalToken((t) => t + 1);
        setShowModal(true);
    };

    const closeModal = () => { setShowModal(false); load(); };

    const eliminar = (pregunta) => {
        Swal.fire({
            title: '¿Eliminar esta pregunta del banco?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sí, eliminar'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await classroomExamService.deleteQuestion(pregunta.questionId);
                    Swal.fire('Eliminada', '', 'success');
                    load();
                } catch (error) {
                    Swal.fire('Ha ocurrido un error', 'Es posible que esté siendo usada en un examen.', 'error');
                }
            }
        });
    };

    if (loading) return <Loading />;

    return (
        <div className="container-fluid p-0 av-module">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h1 className="h3 mb-0">Banco de preguntas</h1>
                <button className="btn btn-primary" onClick={() => openModal({ data: null })}>
                    <i className="fas fa-circle-plus me-1"></i> Nueva pregunta
                </button>
            </div>

            {preguntas.length === 0 ? (
                <div className="alert alert-info">Todavía no hay preguntas. Crea la primera para poder armar exámenes.</div>
            ) : (
                <div className="list-group">
                    {preguntas.map((p) => (
                        <div className="list-group-item d-flex justify-content-between align-items-center" key={p.questionId}>
                            <div>
                                <span className="badge bg-info me-2">{TYPE_LABEL[p.questionTypeId]}</span>
                                <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(p.statementHtml) }} />
                            </div>
                            <div className="btn-group btn-group-sm">
                                <button className="btn btn-outline-info" onClick={() => openModal({ data: p })}><i className="fas fa-pencil-alt"></i></button>
                                <button className="btn btn-outline-danger" onClick={() => eliminar(p)}><i className="fas fa-times"></i></button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <MDBModal tabIndex="-1" show={showModal} setShow={setShowModal}>
                <MDBModalDialog className="modal-dialog modal-lg">
                    <MDBModalContent>
                        <MDBModalHeader>
                            <MDBModalTitle>{modal?.data ? 'Editar pregunta' : 'Nueva pregunta'}</MDBModalTitle>
                            <MDBBtn type="button" className="btn-close" color="none" onClick={() => setShowModal(false)}></MDBBtn>
                        </MDBModalHeader>
                        <MDBModalBody>
                            {modal && <ClassroomQuestionForm key={modalToken} classroomId={Number(classroomId)} question={modal.data} onClose={closeModal} />}
                        </MDBModalBody>
                        <MDBModalFooter>
                            <button type="button" className="btn btn-primary" onClick={() => setShowModal(false)}>Cerrar</button>
                        </MDBModalFooter>
                    </MDBModalContent>
                </MDBModalDialog>
            </MDBModal>
        </div>
    );
};

export default QuestionBank;
